/**
 * @fileoverview Vertical category-column chart with a combined "amount / share" label per bar.
 * @description Ported from the InsightBox Banking design system's own
 * `charts/CategoryColumnChart.tsx` — an alternative to a stacked 100% bar for the same
 * part-to-whole data, trading the at-a-glance "these sum to the whole" read for easier side-by-side
 * magnitude comparison across categories. Render-prop typing uses recharts' own exported
 * `LabelProps` instead of the reference's untyped `any`. Feature-scoped rather than promoted to
 * `shared/` since `CategoryColumnItem` is a banking-specific data shape.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, XAxis, YAxis } from 'recharts'

import { CHART_GRID, CHART_TEXT } from '@/shared/constants/chart-theme.constants'

import type { LabelProps } from 'recharts'

export interface CategoryColumnItem {
  key: string
  label: string
  value: number
  topLabel: string
  color: string
}

function makeTopLabel(data: CategoryColumnItem[]) {
  return function TopLabel(props: LabelProps) {
    const { x, y, width, index } = props
    const point = index === undefined ? undefined : data[index]
    if (!point || typeof x !== 'number' || typeof y !== 'number' || typeof width !== 'number') {
      return null
    }
    return (
      <text
        x={x + width / 2}
        y={y - 8}
        textAnchor="middle"
        className="text-[11px] font-bold"
        fill={CHART_TEXT}
      >
        {point.topLabel}
      </text>
    )
  }
}

interface CategoryColumnChartProps {
  items: CategoryColumnItem[]
  height?: number | '100%'
}

/**
 * @component
 */
function CategoryColumnChart({ items, height = '100%' }: CategoryColumnChartProps) {
  const max = Math.max(...items.map((i) => i.value), 1)
  const TopLabel = makeTopLabel(items)

  return (
    // In fill mode (height="100%"), absolutely fill the nearest positioned ancestor instead of
    // relying on a plain CSS percentage height — see TrendArea.tsx for why. No-op for a fixed
    // numeric height.
    <ResponsiveContainer
      width="100%"
      height={height}
      debounce={200}
      {...(height === '100%' ? { style: { position: 'absolute' as const, inset: 0 } } : {})}
    >
      <BarChart data={items} margin={{ top: 32, right: 16, left: 8, bottom: 8 }}>
        <CartesianGrid stroke={CHART_GRID} vertical={false} />
        <XAxis
          dataKey="label"
          axisLine={false}
          tickLine={false}
          interval={0}
          angle={-20}
          textAnchor="end"
          height={64}
          tick={{ fill: '#111827', fontSize: 11, fontWeight: 500 }}
        />
        <YAxis hide domain={[0, max * 1.28]} />
        <Bar
          dataKey="value"
          radius={[6, 6, 0, 0]}
          maxBarSize={56}
          label={TopLabel}
          isAnimationActive={false}
        >
          {items.map((item) => (
            <Cell key={item.key} fill={item.color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

export default CategoryColumnChart
